import React, { ReactNode, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { Divider } from '../../atoms/divider/Divider'
import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { Title } from '../../atoms/title/Title'
import { getActiveFilterCount } from '../../molecules/taskFilterMenu/activeFilterUtils'
import { TaskFilterMenu, TaskFilterMenuSectionConfig } from '../../molecules/taskFilterMenu/TaskFilterMenu'
import { TaskListItem, TaskListItemProps } from '../../molecules/taskListItem/TaskListItem'
import { TitleSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

export type TaskListPanelProps = {
  filterSections: TaskFilterMenuSectionConfig[]
  items: TaskListItemProps[]
  loadingMessage: ReactNode
  filteredEmptyMessage: ReactNode
  headerRight?: ReactNode
  banner?: ReactNode
  onClearFilters?: () => void
  isLoading?: boolean
}

const Container = styled.View({
  flexShrink: 1,
})

const Header = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingHorizontal: theme.spacing['16px'],
  paddingVertical: theme.spacing['12px'],
}))

const CenteredState = styled.View(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  paddingVertical: theme.spacing['48px'],
  gap: theme.spacing['16px'],
}))

const LoadingState = styled.View(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  paddingVertical: theme.spacing['16px'],
  gap: theme.spacing['8px'],
}))

const ItemList = styled.ScrollView({
  flexShrink: 1,
})

const ClearFiltersButton = styled(PrimaryButton)({
  alignSelf: 'center',
})

const BannerContainer = styled.View(({ theme }) => ({
  paddingHorizontal: theme.spacing['16px'],
  paddingBottom: theme.spacing['12px'],
}))

export const TaskListPanel: React.FC<TaskListPanelProps> = ({
  filterSections,
  items,
  loadingMessage,
  filteredEmptyMessage,
  headerRight,
  banner,
  onClearFilters,
  isLoading = false,
}) => {
  const { colors } = useTheme()

  const hasActiveFilters = useMemo(
    () => getActiveFilterCount(filterSections, { includeRadio: true }) > 0,
    [filterSections],
  )

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingState testID={tID('TaskListPanel-loading')}>
          <LoadingIndicator topPadding={0} />
          <Title size={TitleSize.SMALL} color={colors.textSecondary} text={loadingMessage} />
        </LoadingState>
      )
    }

    if (items.length === 0) {
      return hasActiveFilters ? (
        <CenteredState testID={tID('TaskListPanel-filtered-empty')}>
          <Title size={TitleSize.SMALL} color={colors.textSecondary} text={filteredEmptyMessage} />
          {onClearFilters && (
            <ClearFiltersButton
              testID={tID('TaskListPanel-clear-filters-button')}
              text={
                <FormattedMessage defaultMessage='Clear filters' description='Button to clear all task list filters' />
              }
              onPress={onClearFilters}
            />
          )}
        </CenteredState>
      ) : (
        <CenteredState testID={tID('TaskListPanel-empty')}>
          <Title
            size={TitleSize.SMALL}
            color={colors.textSecondary}
            text={
              <FormattedMessage defaultMessage="You're all caught up!" description='Task list empty state message' />
            }
          />
        </CenteredState>
      )
    }

    return (
      <ItemList testID={tID('TaskListPanel-item-list')}>
        {items.map((item, index) => (
          <React.Fragment key={index}>
            <TaskListItem {...item} />
            {index < items.length - 1 && <Divider height={1} color={colors.dividerSecondary} />}
          </React.Fragment>
        ))}
      </ItemList>
    )
  }

  return (
    <Container>
      <Header>
        <TaskFilterMenu sections={filterSections} />
        {headerRight}
      </Header>
      {!isLoading && banner && <BannerContainer>{banner}</BannerContainer>}
      {renderContent()}
    </Container>
  )
}
